import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import {
  completeStageRevealNavigation,
  isDemoSubscribeRedirectUser,
  runDemoStageSubmitFlow,
  scheduleStagePostSubmitNavigation,
} from '../../utils/demoGate';
import { mergeGameAssignmentIntoUserData } from '../../utils/gameStages';
import { buildCumulativeStageTimes, buildCumulativeStageScore, readCumulativeScoreFromProgress, readCumulativeTimeFromProgress } from '../../utils/stageReportTime';
import { resolvePostSubmitNavigation } from '../../utils/stageNavigation';
import {
  fetchGameStages,
  initStageReport,
  updateStageProgress,
} from '../../functions/stageReport';
import { sendReport } from '../../functions/sendReport';
import { formatGameTimer, parseStageTimerSeconds, resolveCountdownElapsedSeconds } from '../../utils/gameTimer';
import { primeDictionaryWordCache, validateDictionaryWord } from '../../utils/dictionaryValidation';
import { buildStagePageStyle, stageThemeVisuals } from '../../utils/stageThemeVisuals';
import {
  StagePlayGate,
  useStageAlreadyPlayedGuard,
} from '../../utils/useStageAlreadyPlayedGuard';
import { useStageBackButton } from '../../utils/useStageBackButton';
import { getStageRulesList, useStageRulesModal } from '../../utils/useStageRulesModal';
import StagePuzzleReveal from '../../components/StagePuzzleReveal/StagePuzzleReveal';
import '../../components/StagePuzzleReveal/stageLayout.css';
import './word-chain.css';

const STAGE_NUMBER = 4;
const GAME_DISPLAY_TITLE = 'WORD CHAIN';
const POINTS_PER_CHAIN_WORD = 1;
const WORD_MIN = 3;
const WORD_MAX = 40;
const MAX_CHAIN_WORDS = 30;
const REQUIRED_CHAIN_WORDS = 10;
const DEFAULT_PLAY_SECONDS = 180;
const STAGE7_PLAY_DEADLINE_PREFIX = 'stage7_play_deadline';
const DEFAULT_EXAMPLE_CHAIN = ['apple', 'elephant', 'tiger', 'rabbit', 'tomato', 'orange'];

const CHAIN_TILE_COLORS = ['#f5c518', '#7c5bf0', '#4f8ef7', '#5eb8f7', '#3dba6c', '#f59e0b'];

function stage7PlayDeadlineKey(userId, sessionId) {
  return `${STAGE7_PLAY_DEADLINE_PREFIX}_${userId}_${sessionId}`;
}

function readPlayDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return 0;
  try {
    const deadline = Number(sessionStorage.getItem(stage7PlayDeadlineKey(userId, sessionId)) || 0);
    return Number.isFinite(deadline) ? deadline : 0;
  } catch {
    return 0;
  }
}

function setPlayDeadline(user, deadlineMs) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.setItem(stage7PlayDeadlineKey(userId, sessionId), String(deadlineMs));
  } catch {
    /* ignore */
  }
}

function clearPlayDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.removeItem(stage7PlayDeadlineKey(userId, sessionId));
  } catch {
    /* ignore */
  }
}

function clearIntervalTimer(timerRef) {
  if (timerRef.current) {
    window.clearInterval(timerRef.current);
    timerRef.current = null;
  }
}

function normalizeChainWord(raw) {
  const word = String(raw ?? '')
    .replace(/[^a-zA-Z]/g, '')
    .toLowerCase();
  if (!word || word.length < WORD_MIN) return '';
  return word;
}

function parseWordChain(text) {
  return String(text ?? '')
    .split(/(?:→|->|,|;|\n|\r|[\s]+)+/)
    .map(normalizeChainWord)
    .filter(Boolean);
}

function formatStageDescription(raw) {
  return String(raw ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .trim();
}

function ClockIcon({ className = 's7-timer-icon' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8Zm.5-13H11v6l4.7 2.8.8-1.3-4-2.4Z"
      />
    </svg>
  );
}

function ChainTrack({ words, ariaLabel, colorOffset = 0 }) {
  if (!words.length) return null;

  return (
    <div className="s7-chain-track" aria-label={ariaLabel}>
      {words.map((word, index) => (
        <React.Fragment key={`${word}-${index}`}>
          {index > 0 ? (
            <span className="s7-chain-arrow" aria-hidden>
              <i className="fa-solid fa-arrow-right" />
            </span>
          ) : null}
          <span
            className="s7-chain-tile"
            style={{
              backgroundColor:
                CHAIN_TILE_COLORS[(index + colorOffset) % CHAIN_TILE_COLORS.length],
            }}
          >
            {word.toUpperCase()}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

export default function Stage7({ onStageComplete }) {
  const navigate = useNavigate();
  useStageBackButton(7);
  const themeData = useSelector((state) => state.theme.data);
  const user = useSelector((state) => state.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const stage7 = themeData?.stages_data?.stage_4;

  const playSeconds = useMemo(
    () => parseStageTimerSeconds(stage7?.timer, DEFAULT_PLAY_SECONDS),
    [stage7?.timer]
  );

  const logoUrl = s3PublicUrl(stage7?.logo ?? stage7?.badge);
  const rulesList = getStageRulesList(stage7);
  const subtitle = String(stage7?.title ?? stage7?.name ?? "Don't Break the Chain!");
  const descriptionText = useMemo(
    () => formatStageDescription(stage7?.description),
    [stage7?.description]
  );
  const baseChain = useMemo(() => parseWordChain(descriptionText), [descriptionText]);
  const exampleChain = useMemo(
    () => (baseChain.length > 0 ? baseChain : DEFAULT_EXAMPLE_CHAIN),
    [baseChain]
  );

  const isConfigured = !!stage7 && String(stage7?.title ?? '').trim().length > 0;

  const visuals = useMemo(() => stageThemeVisuals(themeData), [themeData]);
  const pageStyle = useMemo(() => buildStagePageStyle(visuals, 's7'), [visuals]);

  const completeSentRef = useRef(false);
  const revealTimeoutRef = useRef(null);
  const nextPathRef = useRef('/puzzle');
  const wordInputRef = useRef(null);
  const playTimerRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const handleSubmitRef = useRef(null);

  const [userWords, setUserWords] = useState([]);
  const [wordDraft, setWordDraft] = useState('');
  const [message, setMessage] = useState('');
  const [inputShake, setInputShake] = useState(false);
  const [validatingWord, setValidatingWord] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showRevealOverlay, setShowRevealOverlay] = useState(false);
  const [revealCompletedDays, setRevealCompletedDays] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);

  const { playCheckReady, alreadyPlayed, beginSubmit } = useStageAlreadyPlayedGuard({
    stageNumber: STAGE_NUMBER,
    submitted,
    user,
    adminToken,
    navigate,
  });

  const stageRulesModal = useStageRulesModal({
    stageNumber: STAGE_NUMBER,
    rulesList,
    isConfigured,
    submitted,
    alreadyPlayed,
    userId: user?.userId,
    sessionId: user?.sessionId,
    ready: !!stage7,
  });
  const { showRules, openRules, closeRules } = stageRulesModal;
  const rulesDismissed = stageRulesModal.rulesDismissed;

  const countdownDisplay = timerReady ? formatGameTimer(secondsLeft) : formatGameTimer(playSeconds);
  const canSubmit = userWords.length >= REQUIRED_CHAIN_WORDS;
  const chainProgressLabel =
    userWords.length >= REQUIRED_CHAIN_WORDS
      ? `${REQUIRED_CHAIN_WORDS} words — ready to submit`
      : `${userWords.length} word${userWords.length === 1 ? '' : 's'}`;
  const atWordGoal = userWords.length >= REQUIRED_CHAIN_WORDS;

  const allUsedWords = useMemo(
    () => [...baseChain, ...userWords],
    [baseChain, userWords]
  );

  const lastChainWord =
    userWords.length > 0 ? userWords[userWords.length - 1] : null;

  useEffect(() => {
    primeDictionaryWordCache(exampleChain);
  }, [exampleChain]);

  useEffect(() => {
    return () => {
      if (revealTimeoutRef.current) {
        window.clearTimeout(revealTimeoutRef.current);
        revealTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setLogoFailed(false);
  }, [logoUrl]);

  useEffect(() => {
    if (!isConfigured || submitted || alreadyPlayed || !playCheckReady || !rulesDismissed) {
      return undefined;
    }

    const userId = user?.userId;
    const sessionId = user?.sessionId;
    if (!userId || !sessionId) return undefined;

    clearIntervalTimer(playTimerRef);

    const finishPlayTime = () => {
      clearIntervalTimer(playTimerRef);
      setSecondsLeft(0);
      setTimerExpired(true);
      clearPlayDeadline(user);
      if (!autoSubmitRef.current) {
        autoSubmitRef.current = true;
        handleSubmitRef.current?.(null, { auto: true });
      }
    };

    const startPlayCountdown = (remainingSeconds) => {
      const safeRemaining = Math.max(0, Math.ceil(Number(remainingSeconds) || 0));
      setTimerReady(true);
      setSecondsLeft(safeRemaining);
      setTimerExpired(safeRemaining <= 0);

      if (safeRemaining <= 0) {
        finishPlayTime();
        return;
      }

      let remaining = safeRemaining;
      playTimerRef.current = window.setInterval(() => {
        remaining -= 1;
        const next = Math.max(0, remaining);
        setSecondsLeft(next);
        if (next <= 0) {
          finishPlayTime();
        }
      }, 1000);
    };

    const now = Date.now();
    let playDeadline = readPlayDeadline(user);
    if (!playDeadline) {
      playDeadline = now + playSeconds * 1000;
      setPlayDeadline(user, playDeadline);
    }

    if (now >= playDeadline) {
      setTimerReady(true);
      finishPlayTime();
      return undefined;
    }

    startPlayCountdown((playDeadline - now) / 1000);

    return () => clearIntervalTimer(playTimerRef);
  }, [
    alreadyPlayed,
    isConfigured,
    playCheckReady,
    playSeconds,
    rulesDismissed,
    submitted,
    user,
    user?.userId,
    user?.sessionId,
  ]);

  const triggerShake = () => {
    setInputShake(true);
    window.setTimeout(() => setInputShake(false), 500);
  };

  const handleAddWord = async (e) => {
    e?.preventDefault?.();
    if (submitted || timerExpired || validatingWord) return;

    const word = normalizeChainWord(wordDraft);
    if (!word) {
      triggerShake();
      setMessage(`Enter a valid word (${WORD_MIN}+ letters)`);
      return;
    }

    if (allUsedWords.some((w) => w.toLowerCase() === word)) {
      setMessage('You already used that word');
      return;
    }

    if (userWords.length > 0 && lastChainWord) {
      const lastLetter = lastChainWord.slice(-1);
      if (word[0] !== lastLetter) {
        triggerShake();
        setMessage(`Word must start with "${lastLetter.toUpperCase()}"`);
        return;
      }
    }

    if (userWords.length >= REQUIRED_CHAIN_WORDS) {
      setMessage('You have 10 words — submit your chain');
      return;
    }

    if (userWords.length >= MAX_CHAIN_WORDS) {
      setMessage(`Maximum ${MAX_CHAIN_WORDS} words in the chain`);
      return;
    }

    setValidatingWord(true);
    setMessage('Checking dictionary…');

    try {
      const dictionaryResult = await validateDictionaryWord(word);
      if (!dictionaryResult.valid) {
        triggerShake();
        setMessage(dictionaryResult.message);
        return;
      }

      setUserWords((prev) => [...prev, word]);
      setWordDraft('');
      setMessage('');
      window.setTimeout(() => wordInputRef.current?.focus(), 0);
    } finally {
      setValidatingWord(false);
    }
  };

  const handleRemoveLastWord = () => {
    if (submitted || !userWords.length) return;
    setUserWords((prev) => prev.slice(0, -1));
    if (message) setMessage('');
  };

  const handleSubmit = async (e, { auto = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    if (submitted) return;

    if (!userWords.length && !auto) {
      triggerShake();
      setMessage('Add at least one word to your chain');
      return;
    }

    if (!auto && userWords.length < REQUIRED_CHAIN_WORDS) {
      triggerShake();
      setMessage(`Add at least ${REQUIRED_CHAIN_WORDS} words before submitting`);
      return;
    }

    const playDeadline = readPlayDeadline(user);
    const stageTimeSeconds = resolveCountdownElapsedSeconds({
      totalSeconds: playSeconds,
      secondsLeft,
      deadlineMs: playDeadline,
    });

    clearIntervalTimer(playTimerRef);
    clearPlayDeadline(user);

    const trimmedAnswer = allUsedWords.join(' → ');

    const userId = user?.userId;
    const organizationId = user?.organizationId;
    const sessionId = user?.sessionId;
    const token =
      adminToken ||
      (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

    if (!token || !userId || !organizationId || !sessionId) {
      setMessage('Unable to save your response. Please sign in again.');
      return;
    }

    const chainWordCount = Math.min(userWords.length, REQUIRED_CHAIN_WORDS);
    const finalPoints = chainWordCount * POINTS_PER_CHAIN_WORD;

    if (isDemoSubscribeRedirectUser(user)) {
      runDemoStageSubmitFlow({
        user,
        navigate,
        revealTimeoutRef,
        nextPathRef,
        beginSubmit,
        markSubmitted: () => setSubmitted(true),
        setMessage,
        setRevealCompletedDays,
        setShowRevealOverlay,
        completedDaysForReveal: STAGE_NUMBER,
      });
      if (typeof onStageComplete === 'function' && !completeSentRef.current) {
        completeSentRef.current = true;
        onStageComplete({
          earnedPoints: finalPoints,
          answer: trimmedAnswer,
          allCorrect: chainWordCount > 0,
        });
      }
      return;
    }

    setMessage('');
    beginSubmit();
    setSubmitted(true);

    try {
      await initStageReport({
        token,
        userId,
        email: user?.email,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
        totalPuzzles: themeData?.total_puzzles ?? 9,
      });

      const stageData = await fetchGameStages({ token, userId, organizationId, sessionId });
      const game = stageData?.game ?? null;
      const nextDay = stageData?.nextDay != null ? Number(stageData.nextDay) : null;
      if (!Number.isFinite(nextDay) || nextDay < 1) {
        throw new Error('No active day found for this stage');
      }

      mergeGameAssignmentIntoUserData({
        game,
        currentStage: stageData?.currentStage,
        nextDay: nextDay + 1,
      });

      const reportTimes = buildCumulativeStageTimes(stageData, stageTimeSeconds);
      const reportScores = buildCumulativeStageScore(stageData, finalPoints);

      const progressResult = await updateStageProgress({
        token,
        userId,
        dayNumber: STAGE_NUMBER,
        points: finalPoints,
        timeSeconds: reportTimes.currentTimeSeconds,
        answer: trimmedAnswer,
        allCorrect: chainWordCount > 0,
        email: user?.email,
        name: user?.name,
      });

      sendReport({
        token,
        userId,
        email: user?.email,
        points: readCumulativeScoreFromProgress(progressResult) ?? reportScores.cumulativeScore,
        accumulatePoints: false,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
        time: readCumulativeTimeFromProgress(progressResult) ?? reportTimes.cumulativeTimeSeconds,
      });
    } catch (err) {
      setSubmitted(false);
      autoSubmitRef.current = false;
      setMessage(err?.message || 'Failed to save your response. Please try again.');
      return;
    }

    let path = '/puzzle';
    let completedDaysForReveal = 1;
    try {
      const { path: nextPath, completedDaysForReveal: revealDays } =
        await resolvePostSubmitNavigation({
          stageNumber: STAGE_NUMBER,
          token,
          userId,
          organizationId,
          sessionId,
        });
      path = nextPath;
      completedDaysForReveal = revealDays;
    } catch {
      path = '/puzzle';
    }

    if (typeof onStageComplete === 'function' && !completeSentRef.current) {
      completeSentRef.current = true;
      onStageComplete({
        earnedPoints: finalPoints,
        answer: trimmedAnswer,
        allCorrect: chainWordCount > 0,
      });
    }

    nextPathRef.current = path;
    setRevealCompletedDays(completedDaysForReveal);
    setShowRevealOverlay(true);
    scheduleStagePostSubmitNavigation({
      user,
      navigate,
      path,
      revealTimeoutRef,
    });
  };

  handleSubmitRef.current = handleSubmit;

  const revealConfettiPieces = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        left: `${Math.round((i / 19) * 100)}%`,
        delay: `${(i % 7) * 0.08}s`,
        duration: `${2.1 + (i % 5) * 0.22}s`,
      })),
    []
  );

  const handleRevealOverlayClick = (ev) => {
    if (ev.target !== ev.currentTarget) return;
    completeStageRevealNavigation({
      user,
      navigate,
      path: nextPathRef.current || '/puzzle',
      revealTimeoutRef,
    });
  };

  return (
    <div className="stage7-page" style={pageStyle}>
      <StagePlayGate
        playCheckReady={playCheckReady}
        alreadyPlayed={alreadyPlayed}
        isConfigured={isConfigured}
        notConfiguredMessage={
          <p className="main-day-subtitle">
            Stage 4 is not configured: set stage_4 title and description in the theme.
          </p>
        }
      >
        <>
          <div className="stage-page-main stage7-header-main">
            <header className="s7-top-bar">
              <div className="s7-top-timer" aria-label="Stage timer" aria-live="polite">
                <ClockIcon />
                <span className="s7-timer-value">{countdownDisplay}</span>
              </div>
              {rulesList.length > 0 ? (
                <button
                  type="button"
                  className="stage7-rules-fab"
                  onClick={openRules}
                  aria-label="How to play"
                  title="How to play"
                >
                  ?
                </button>
              ) : null}
            </header>
          </div>

          <div className="stage-page-main stage7-main">
          <div className="s7-heading s7-heading--center">
            <div className="s7-title-row">
              <span className="s7-title-deco" aria-hidden>
                ✦ ✦ ✦
              </span>
              <div className="s7-game-title">
                {logoUrl && !logoFailed ? (
                  <img
                    src={logoUrl}
                    alt={GAME_DISPLAY_TITLE}
                    className="s7-game-logo"
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <h1 className="s7-game-title-text">{GAME_DISPLAY_TITLE}</h1>
                )}
              </div>
              <span className="s7-title-deco" aria-hidden>
                ✦ ✦ ✦
              </span>
            </div>
            {subtitle ? <p className="s7-game-subtitle">{subtitle}</p> : null}
            <span className="s7-subtitle-line" aria-hidden />
          </div>

          <div className="s7-game-row">
            <div className="s7-play-area">
              <div className="s7-chain-stack">
                <div className="s7-chain-board s7-chain-board--example">
                  <p className="s7-chain-example-label">Example</p>
                  <ChainTrack words={exampleChain} ariaLabel="Example word chain" />
                </div>

                <div
                  className={`s7-chain-board s7-chain-board--user${userWords.length ? '' : ' s7-chain-board--empty'}`}
                >
                  <ChainTrack words={userWords} ariaLabel="Your word chain" />
                </div>
              </div>

              {!submitted ? (
                <form id="stage7-chain-form" className="s7-chain-form" onSubmit={handleSubmit}>
                  <div
                    className={`s7-word-input-row${inputShake ? ' s7-word-input-row--shake' : ''}`}
                  >
                    <input
                      ref={wordInputRef}
                      id="stage7-word"
                      type="text"
                      className="s7-word-input"
                      value={wordDraft}
                      onChange={(ev) => {
                        setWordDraft(ev.target.value.slice(0, WORD_MAX));
                        if (message) setMessage('');
                      }}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter') {
                          ev.preventDefault();
                          handleAddWord();
                        }
                      }}
                      maxLength={WORD_MAX}
                      placeholder={
                        lastChainWord
                          ? `Starts with “${lastChainWord.slice(-1).toUpperCase()}”…`
                          : 'First word…'
                      }
                      disabled={submitted || timerExpired || validatingWord || atWordGoal}
                      aria-label="Add word to chain"
                      aria-busy={validatingWord}
                    />
                    <button
                      type="button"
                      className="s7-word-add-btn"
                      onClick={handleAddWord}
                      disabled={
                        submitted ||
                        timerExpired ||
                        validatingWord ||
                        atWordGoal ||
                        !String(wordDraft ?? '').trim()
                      }
                    >
                      {validatingWord ? 'Checking…' : 'Add'}
                    </button>
                    {userWords.length > 0 ? (
                      <button
                        type="button"
                        className="s7-word-undo-btn"
                        onClick={handleRemoveLastWord}
                        disabled={submitted}
                      >
                        Undo
                      </button>
                    ) : null}
                  </div>
                  <p className="s7-chain-progress" aria-live="polite">
                    {chainProgressLabel}
                  </p>
                  <button
                    type="submit"
                    className="s7-submit-btn"
                    disabled={submitted || !canSubmit}
                  >
                    Submit
                  </button>
                </form>
              ) : (
                <p className="s7-complete">Thanks! Your word chain has been submitted.</p>
              )}

              {message ? <div className="s7-hint">{message}</div> : null}
            </div>

            <div className="stage-page-puzzle-aside stage7-puzzle-aside">
              <StagePuzzleReveal themeData={themeData} />
            </div>
          </div>
          </div>
        </>
      </StagePlayGate>

      {showRules && rulesList.length > 0 ? (
        <div
          className="stage7-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stage7-rules-title"
          onClick={closeRules}
        >
          <div className="stage7-modal" onClick={(ev) => ev.stopPropagation()}>
            <h2 id="stage7-rules-title">How to play</h2>
            <ul className="stage7-rules-list">
              {rulesList.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
            <div className="stage7-modal-actions">
              <button type="button" onClick={closeRules}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showRevealOverlay ? (
        <div
          className="stage-reveal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Revealing your puzzle progress"
          onClick={handleRevealOverlayClick}
        >
          <div className="stage-reveal-confetti" aria-hidden>
            {revealConfettiPieces.map((piece) => (
              <span
                key={piece.id}
                className="stage-reveal-confetti-piece"
                style={{
                  left: piece.left,
                  animationDelay: piece.delay,
                  animationDuration: piece.duration,
                }}
              />
            ))}
          </div>
          <div className="stage-reveal-stage">
            <aside className="stage-reveal-panel" aria-label="Puzzle pieces progress">
              <StagePuzzleReveal
                themeData={themeData}
                completedDaysOverride={revealCompletedDays}
                animateReveal
              />
            </aside>
          </div>
        </div>
      ) : null}
    </div>
  );
}
